import React, { useState, useRef, useEffect } from 'react';
import { Card, Descriptions, Typography, Spin, message, Button, Switch, Input, InputNumber, Space } from 'antd';
import { UserOutlined, MailOutlined, SafetyOutlined, EnvironmentOutlined, TeamOutlined, PictureOutlined } from '@ant-design/icons';
import { apiService } from '../services/apiService';
import { getGlSettings, updateGlSettings } from '../services/glService';
import { useResponsive } from '../hooks/useResponsive';
import './AdminProfile.css';

const { Title, Text } = Typography;

interface ProfileRole {
  id: number;
  name: string;
  description?: string;
}

interface ProfileLocation {
  id: number;
  name: string;
  code: string;
  address?: string;
}

interface ProfileCompany {
  id?: number;
  name?: string;
  logo_file_path?: string;
  gst_registered?: boolean;
  gst_prices_inclusive?: boolean;
  tax_id?: string | null;
}

interface AdminProfileData {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  role: ProfileRole;
  location: ProfileLocation | null;
  company?: ProfileCompany | null;
  is_active: boolean;
  created_at: string;
}

const AdminProfile: React.FC = () => {
  const { isMobile } = useResponsive();
  const [profile, setProfile] = useState<AdminProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [gstSaving, setGstSaving] = useState(false);
  const [gstRegistered, setGstRegistered] = useState(false);
  const [gstInclusive, setGstInclusive] = useState(true);
  const [gstRate, setGstRate] = useState(5);
  const [gstBuyerThreshold, setGstBuyerThreshold] = useState(50000);
  const [gstTpn, setGstTpn] = useState('');
  const [glEnabled, setGlEnabled] = useState(false);
  const [glSaving, setGlSaving] = useState(false);
  const [payAccountName, setPayAccountName] = useState('');
  const [payAccountNumber, setPayAccountNumber] = useState('');
  const [payAccountBank, setPayAccountBank] = useState('');
  const [paySaving, setPaySaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const data = await apiService.get<AdminProfileData>('/auth/me');
      setProfile(data);
      try {
        const gst = await apiService.get<{
          gst_registered: boolean;
          gst_prices_inclusive: boolean;
          gst_rate?: number;
          gst_buyer_threshold?: number;
          tax_id?: string | null;
        }>('/company-admin/gst-settings');
        setGstRegistered(Boolean(gst.gst_registered));
        setGstInclusive(gst.gst_prices_inclusive !== false);
        setGstRate(Number(gst.gst_rate ?? 5));
        setGstBuyerThreshold(Number(gst.gst_buyer_threshold ?? 50000));
        setGstTpn(gst.tax_id || '');
      } catch {
        setGstRegistered(Boolean(data.company?.gst_registered));
        setGstInclusive(data.company?.gst_prices_inclusive !== false);
        setGstRate(5);
        setGstBuyerThreshold(50000);
        setGstTpn(data.company?.tax_id || '');
      }
      try {
        const gl = await getGlSettings();
        setGlEnabled(Boolean(gl.gl_enabled));
      } catch {
        setGlEnabled(false);
      }
      try {
        const pay = await apiService.get<{
          payment_account_name?: string | null;
          payment_account_number?: string | null;
          payment_account_bank?: string | null;
        }>('/company-admin/payment-settings');
        setPayAccountName(pay.payment_account_name || '');
        setPayAccountNumber(pay.payment_account_number || '');
        setPayAccountBank(pay.payment_account_bank || '');
      } catch {
        setPayAccountName('');
        setPayAccountNumber('');
        setPayAccountBank('');
      }
    } catch (error) {
      message.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.match(/^image\/(png|jpeg|jpg)$/)) {
      message.error('Please select a PNG or JPEG image.');
      return;
    }
    setLogoUploading(true);
    try {
      const formData = new FormData();
      formData.append('logo_file', file);
      await apiService.upload('/auth/me/company-logo', formData);
      window.dispatchEvent(new CustomEvent('company-logo-updated'));
      message.success('Company logo uploaded. It will appear on PO PDFs and in the sidebar.');
      loadProfile();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setLogoUploading(false);
      e.target.value = '';
    }
  };

  const handleSaveGst = async () => {
    if (gstRegistered && !gstTpn.trim()) {
      message.error('Taxpayer Number (TPN) is required to issue GST tax invoices.');
      return;
    }
    setGstSaving(true);
    try {
      await apiService.put('/company-admin/gst-settings', {
        gst_registered: gstRegistered,
        gst_prices_inclusive: gstInclusive,
        gst_rate: gstRate,
        gst_buyer_threshold: gstBuyerThreshold,
        tax_id: gstTpn.trim() || null,
      });
      message.success(
        gstRegistered
          ? 'GST is on. POS will print TAX INVOICE. GST is charged to the customer; the shop collects it for DRC filing.'
          : 'GST is off. POS receipts are unchanged.',
      );
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Failed to save GST settings');
    } finally {
      setGstSaving(false);
    }
  };

  const handleSaveGl = async () => {
    setGlSaving(true);
    try {
      const result = await updateGlSettings(glEnabled);
      setGlEnabled(Boolean(result.gl_enabled));
      message.success(
        result.gl_enabled
          ? `General Ledger is on. Chart of accounts ready (${result.account_count} accounts). POS and GRPO will post journals.`
          : 'General Ledger is off. Operational sales and purchases are unchanged.',
      );
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Failed to save GL settings');
    } finally {
      setGlSaving(false);
    }
  };

  const handleSavePayment = async () => {
    setPaySaving(true);
    try {
      await apiService.put('/company-admin/payment-settings', {
        payment_account_name: payAccountName.trim() || null,
        payment_account_number: payAccountNumber.trim() || null,
        payment_account_bank: payAccountBank.trim() || null,
      });
      message.success('Mobile payment account saved. POS will show it on the amount QR screen.');
    } catch (err: any) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 404) {
        message.error(
          'Payment settings API not found. Restart the backend (backend\\start-dev.ps1) so /company-admin/payment-settings is loaded.'
        );
      } else {
        message.error(typeof detail === 'string' ? detail : 'Failed to save payment settings');
      }
    } finally {
      setPaySaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-profile-loading">
        <Spin size="large" tip="Loading profile..." />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="admin-profile">
        <Card>
          <Text type="secondary">Unable to load your profile.</Text>
        </Card>
      </div>
    );
  }

  const fullName = [profile.first_name, profile.last_name].filter(Boolean).join(' ') || profile.username;

  return (
    <div className="admin-profile">
      <Title level={2} style={{ marginBottom: 24 }}>
        My Profile
      </Title>

      <Card
        className="admin-profile-card"
        title={
          <span>
            <UserOutlined style={{ marginRight: 8 }} />
            Personal information
          </span>
        }
      >
        <Descriptions
          column={isMobile ? 1 : 2}
          bordered
          size={isMobile ? 'small' : 'middle'}
          labelStyle={{ fontWeight: 600, width: isMobile ? '40%' : 160 }}
        >
          <Descriptions.Item label="Name">{fullName}</Descriptions.Item>
          <Descriptions.Item label="Username">{profile.username}</Descriptions.Item>
          <Descriptions.Item label="Email" span={2}>
            <MailOutlined style={{ marginRight: 6 }} />
            {profile.email}
          </Descriptions.Item>
          <Descriptions.Item label="Role">
            <SafetyOutlined style={{ marginRight: 6 }} />
            {profile.role?.name ?? '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Text type={profile.is_active ? 'success' : 'secondary'}>
              {profile.is_active ? 'Active' : 'Inactive'}
            </Text>
          </Descriptions.Item>
          {profile.location && (
            <Descriptions.Item label="Location" span={2}>
              <EnvironmentOutlined style={{ marginRight: 6 }} />
              {profile.location.name} ({profile.location.code})
              {profile.location.address && ` — ${profile.location.address}`}
            </Descriptions.Item>
          )}
          {profile.company?.name && (
            <Descriptions.Item label="Company" span={2}>
              <TeamOutlined style={{ marginRight: 6 }} />
              {profile.company.name}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Member since">
            {profile.created_at
              ? new Date(profile.created_at).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })
              : '—'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {profile.company && (
        <Card
          className="admin-profile-card"
          style={{ marginTop: 16 }}
          title={
            <span>
              <PictureOutlined style={{ marginRight: 8 }} />
              Company Logo
            </span>
          }
        >
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            Upload your company logo (PNG or JPEG). It appears on sales receipts, purchase order PDFs, and in the sidebar.
          </Text>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg"
            style={{ display: 'none' }}
            onChange={handleLogoUpload}
          />
          <Button
            type="primary"
            icon={<PictureOutlined />}
            loading={logoUploading}
            onClick={() => fileInputRef.current?.click()}
          >
            {profile.company?.logo_file_path ? 'Change Logo' : 'Upload Logo'}
          </Button>
        </Card>
      )}

      {profile.company?.id ? (
        <Card title="GST / tax invoices" style={{ marginTop: 16 }}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            GST is a consumption tax: the customer pays it. This shop only collects GST and
            remits net GST (output minus input credits) to DRC. Leave this off until the
            business is GST-registered. Save TPN here for filing — it is not printed on the
            customer receipt.
          </Text>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Issue Bhutan GST tax invoices
              </Text>
              <Switch checked={gstRegistered} onChange={setGstRegistered} />
            </div>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Selling prices already include GST
              </Text>
              <Switch checked={gstInclusive} onChange={setGstInclusive} disabled={!gstRegistered} />
              <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                Keep this on so the shelf price already includes the GST the customer pays.
                Turn off only if you quote prices before GST and add the rate at the till.
              </Text>
            </div>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                GST rate (%)
              </Text>
              <InputNumber
                min={0}
                max={100}
                step={0.01}
                value={gstRate}
                onChange={(v) => setGstRate(Number(v ?? 5))}
                disabled={!gstRegistered}
                style={{ width: 160 }}
              />
              <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                Default for taxable products. Product-level rate still overrides when set.
              </Text>
            </div>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Buyer ID required from (Nu.)
              </Text>
              <InputNumber
                min={0}
                step={1000}
                value={gstBuyerThreshold}
                onChange={(v) => setGstBuyerThreshold(Number(v ?? 50000))}
                disabled={!gstRegistered}
                style={{ width: 200 }}
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => Number(String(value || '0').replace(/,/g, '')) as unknown as 0}
              />
              <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                Reference amount for GST filing reports. Buyer CID/TPN are optional at POS.
              </Text>
            </div>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Taxpayer Number (TPN)
              </Text>
              <Input
                value={gstTpn}
                onChange={(e) => setGstTpn(e.target.value)}
                placeholder="Required on TAX INVOICE"
                disabled={!gstRegistered}
              />
            </div>
            <Button type="primary" loading={gstSaving} onClick={handleSaveGst}>
              Save GST settings
            </Button>
          </Space>
        </Card>
      ) : null}

      {profile.company?.id ? (
        <Card title="Mobile / bank payment (POS QR)" style={{ marginTop: 16 }}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Shown on the till when the cashier chooses Mobile (mBoB / bank app). Customers see the
            exact Nu amount QR and your account details, then the cashier enters the bank journal
            number and the payer&apos;s phone (to call if the credit does not appear).
          </Text>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Account name (payee)
              </Text>
              <Input
                value={payAccountName}
                onChange={(e) => setPayAccountName(e.target.value)}
                placeholder={profile.company?.name || 'Shop or account holder name'}
              />
            </div>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Bank / wallet
              </Text>
              <Input
                value={payAccountBank}
                onChange={(e) => setPayAccountBank(e.target.value)}
                placeholder="e.g. Bank of Bhutan, BNB, DK Bank"
              />
            </div>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Account number
              </Text>
              <Input
                value={payAccountNumber}
                onChange={(e) => setPayAccountNumber(e.target.value)}
                placeholder="Account number for mBoB / bank transfer"
              />
            </div>
            <Button type="primary" loading={paySaving} onClick={handleSavePayment}>
              Save payment account
            </Button>
          </Space>
        </Card>
      ) : null}

      {profile.company?.id ? (
        <Card title="General Ledger" style={{ marginTop: 16 }}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            When enabled, completed POS sales/refunds and GRPOs automatically post balanced
            journals. Leave off until you are ready to use Trial Balance and Profit &amp; Loss.
          </Text>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Enable General Ledger
              </Text>
              <Switch checked={glEnabled} onChange={setGlEnabled} />
            </div>
            <Button type="primary" loading={glSaving} onClick={handleSaveGl}>
              Save GL settings
            </Button>
          </Space>
        </Card>
      ) : null}
    </div>
  );
};

export default AdminProfile;
